import type { Dispatch, SetStateAction } from 'react';
import { useWatch } from 'react-hook-form';
import { useFormContext } from 'app/hooks/form';
import { useAllAddresses } from 'app/network/address';
import { useListingVisibilityGroups } from 'app/network/groups';
import type { CreateListingDTO } from 'app/types/listing';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import type { ListingsVisibilityExclusionsPreference } from '../index';
import { ListingVisibilitySelector } from './listing-visibility-selector';

export function GroupsSection({
  alwaysShow = false,
  includeAllSectorGroups = false,
  setListingsVisibilityExclusionsPreference,
}: {
  alwaysShow?: boolean;
  includeAllSectorGroups?: boolean;
  setListingsVisibilityExclusionsPreference?: Dispatch<SetStateAction<ListingsVisibilityExclusionsPreference>>;
}) {
  const { data: allGroups } = useListingVisibilityGroups(includeAllSectorGroups);
  const { data: allAddresses } = useAllAddresses();
  const { control, setValue } = useFormContext<CreateListingDTO>();
  const addressIds = useWatch({ control, name: 'addressIds' }) ?? [];
  const groupIds = useWatch({ control, name: 'groupIds' }) ?? [];

  if (isFeatureEnabled(FeatureFlag.justOneClub)) return null;
  if (!allGroups?.length && (allAddresses?.length ?? 0) <= 1 && !alwaysShow) return null;

  return (
    <ListingVisibilitySelector
      addresses={allAddresses ?? []}
      groups={allGroups ?? []}
      value={{ addressIds, groupIds }}
      showGroupsHeading={!isWhiteLabel(WhiteLabel.Refrain)}
      onChange={(selection) => {
        setValue('addressIds', selection.addressIds, { shouldDirty: true, shouldValidate: true });
        setValue('groupIds', selection.groupIds, { shouldDirty: true, shouldValidate: true });
        if (allAddresses && allGroups && setListingsVisibilityExclusionsPreference) {
          setListingsVisibilityExclusionsPreference({
            addressIds: allAddresses.filter(({ id }) => !selection.addressIds.includes(id)).map(({ id }) => id),
            groupIds: allGroups.filter(({ id }) => !selection.groupIds.includes(id)).map(({ id }) => id),
          });
        }
      }}
    />
  );
}
